import React from "react"
import { twMerge } from "tailwind-merge"

import AnimatedSection from "../../ProProfileShared/AnimatedSection"
import { applyButtonTheme, getButtonClasses } from "../utils/themeUtils"
import { buildServiceUrl, buildBookingUrl, buildPublicPageUrl } from "../utils/urlUtils"
import { DEFAULT_HIGHLIGHT_CLASSES } from "../WebsiteBuilderV2Page"

const AboutSectionMobile = ({ section, practice, theme, highlightedField }) => {
  const isHighlighted = highlightedField === `section-${section.id}`
  const settings = section.settings || {}
  const layout = settings.layout || "image_left"
  const showCTA = settings.showCTA !== undefined ? settings.showCTA : true
  const ctaText = settings.ctaText || "Learn more"
  const ctaAction = settings.ctaAction || "page"
  const ctaTarget = settings.ctaTarget || ""
  const showBackgroundColor = settings.showBackgroundColor || false
  const sectionBackgroundColor = settings.backgroundColor

  const resolveSectionBackgroundColor = () => {
    const defaultColor = "#F4ECE9"
    const themeSecondary = theme?.colors?.secondary
    if (showBackgroundColor && sectionBackgroundColor) return sectionBackgroundColor
    return themeSecondary || defaultColor
  }

  const handleCTAClick = () => {
    switch (ctaAction) {
      case "page":
        if (ctaTarget) {
          window.location.href = buildPublicPageUrl(ctaTarget, practice)
        }
        break
      case "service":
        if (ctaTarget) {
          window.open(buildServiceUrl(ctaTarget, practice), "_blank")
        }
        break
      case "booking":
        window.open(buildBookingUrl(practice), "_blank")
        break
      case "contact":
        document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" })
        break
      case "scroll":
        window.scrollBy({ top: window.innerHeight, behavior: "smooth" })
        break
      case "external":
        break
      default:
        break
    }
  }

  const renderContent = () => (
    <div className="flex w-full max-w-full flex-col gap-6">
      {/* Text Content */}
      <div className="flex w-full flex-col gap-6">
        {section.headline && (
          <h2
            className="w-full text-[24px] font-bold leading-[28px]"
            style={{
              fontFamily: theme?.fonts?.heading || "Playfair Display, serif",
              fontWeight: theme?.fonts?.headingWeight || "700",
              color: theme?.colors?.textDark || "#55433F"
            }}>
            {section.headline}
          </h2>
        )}
        {section.body && (
          <>
            <style>{`
              .about-section-content ul {
                list-style-type: disc !important;
                margin-left: 1.5rem !important;
                margin-bottom: 1rem !important;
              }
              .about-section-content li {
                margin-bottom: 0.5rem !important;
                display: list-item !important;
              }
            `}</style>
            <div
              className="about-section-content prose prose-lg w-full max-w-none text-[16px] leading-[22px]"
              style={{
                fontFamily: theme?.fonts?.paragraph || "Work Sans, sans-serif",
                fontWeight: theme?.fonts?.paragraphRegularWeight || "400",
                color: theme?.colors?.textDark || "#55433F"
              }}
              dangerouslySetInnerHTML={{ __html: section.body }}
            />
          </>
        )}
      </div>

      {/* CTA Button */}
      {showCTA && (
        <button
          onClick={handleCTAClick}
          className={twMerge(
            "inline-flex items-center justify-center px-10 py-3 text-[16px] font-normal leading-[24px] text-white transition-all hover:shadow-lg",
            getButtonClasses(theme?.buttonStyle)
          )}
          style={applyButtonTheme(
            {
              backgroundColor: theme?.colors?.primary || "#BD654F",
              fontFamily: theme?.fonts?.paragraph || "Work Sans, sans-serif"
            },
            theme
          )}
          onMouseEnter={(e) => {
            e.target.style.backgroundColor = theme?.colors?.primaryDark || "#A55A45"
          }}
          onMouseLeave={(e) => {
            e.target.style.backgroundColor = theme?.colors?.primary || "#BD654F"
          }}>
          {ctaText}
        </button>
      )}
    </div>
  )

  // Mobile-specific layouts
  switch (layout) {
    case "image_left":
    case "image_right":
      return (
        <section
          className={twMerge(
            "relative flex items-center justify-start",
            isHighlighted ? DEFAULT_HIGHLIGHT_CLASSES : ""
          )}
          style={{ backgroundColor: resolveSectionBackgroundColor() }}>
          <AnimatedSection className="mx-auto w-full px-4 py-8">
            <div className="flex w-full flex-col items-center gap-5">
              {/* Mobile Image */}
              {section.imageUrl ? (
                <div className="relative w-full">
                  <div
                    className="h-[320px] w-full rounded-lg bg-cover bg-center"
                    style={{ backgroundImage: `url(${section.imageUrl})` }}
                  />
                </div>
              ) : (
                <div className="relative w-full">
                  <div className="flex h-[320px] w-full items-center justify-center rounded-lg bg-gray-200">
                    <span className="text-gray-500">No image selected</span>
                  </div>
                </div>
              )}
              <div className="w-full flex-1">{renderContent()}</div>
            </div>
          </AnimatedSection>
        </section>
      )

    case "centered":
      return (
        <section
          className={twMerge(
            "relative flex min-h-[400px] items-center justify-center py-8",
            isHighlighted ? DEFAULT_HIGHLIGHT_CLASSES : ""
          )}
          style={{ backgroundColor: resolveSectionBackgroundColor() }}>
          <AnimatedSection className="mx-auto w-full px-4 text-center">
            {section.imageUrl && (
              <div className="mb-6 flex justify-center">
                <div className="relative">
                  <div
                    className="absolute -bottom-4 -right-4 h-16 w-12 rounded-lg"
                    style={{ backgroundColor: theme?.colors?.accent || "#E6DAD7" }}
                  />
                  <img
                    src={section.imageUrl}
                    alt={section.headline || "About image"}
                    className="relative h-48 w-48 rounded-lg object-cover"
                  />
                </div>
              </div>
            )}
            {renderContent()}
          </AnimatedSection>
        </section>
      )

    case "no_image":
    default:
      return (
        <section
          className={twMerge(
            "relative flex min-h-[400px] items-center justify-center py-8",
            isHighlighted ? DEFAULT_HIGHLIGHT_CLASSES : ""
          )}
          style={{ backgroundColor: resolveSectionBackgroundColor() }}>
          <AnimatedSection className="mx-auto w-full px-4">{renderContent()}</AnimatedSection>
        </section>
      )
  }
}

export default AboutSectionMobile
